import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Clock, AlertTriangle, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(d / (1000 * 60 * 60 * 24)));
}

const ZELLE_EMAIL = "stressfreeaging@gmail.com";
const PRICE = 299;

export default function MembershipCard({ facility }) {
  const [copied, setCopied] = useState(false);
  if (!facility) return null;

  const status = facility.membership_status || "trial";
  const daysLeftActive = daysUntil(facility.membership_expires_at);
  const daysLeftTrial = daysUntil(facility.trial_ends_at);
  const isActive = status === "active" && daysLeftActive > 0;
  const isTrial = status === "trial" && daysLeftTrial > 0;
  const expired = !isActive && !isTrial;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(ZELLE_EMAIL);
      setCopied(true);
      toast.success("Zelle email copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  return (
    <Card data-testid="membership-card" className="rounded-2xl border border-emerald/30 bg-gradient-to-br from-emerald/5 to-card p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-emerald">Providence Partners membership</p>
          <h2 className="mt-2 font-serif text-2xl text-forest">${PRICE} / year per location</h2>
          {isActive && (
            <Badge variant="outline" data-testid="membership-active-badge" className="mt-2 rounded-full border-emerald/50 bg-emerald/10 text-emerald">
              <CheckCircle2 className="mr-1 h-3 w-3" /> Active — {daysLeftActive} days left
            </Badge>
          )}
          {isTrial && (
            <Badge variant="outline" data-testid="membership-trial-badge" className="mt-2 rounded-full border-gold/60 bg-gold/10 text-gold">
              <Clock className="mr-1 h-3 w-3" /> Free trial — {daysLeftTrial} days left
            </Badge>
          )}
          {expired && (
            <Badge variant="outline" data-testid="membership-expired-badge" className="mt-2 rounded-full border-destructive/40 bg-destructive/10 text-destructive">
              <AlertTriangle className="mr-1 h-3 w-3" /> Membership expired — your facility is hidden from public search
            </Badge>
          )}
        </div>
        <ShieldCheck className="h-8 w-8 text-emerald" strokeWidth={1.5} />
      </div>

      <ul className="mt-5 grid gap-2 text-sm text-foreground sm:grid-cols-2">
        <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald" /> Listed publicly in the locator</li>
        <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald" /> Receive caregiver inquiries</li>
        <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald" /> 3–5 facility photos in object storage</li>
        <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald" /> Specialty badges (Memory Care, etc.)</li>
      </ul>

      {(isTrial || expired) && (
        <div data-testid="zelle-payment-card" className="mt-6 rounded-2xl border border-gold/40 bg-gold/5 p-5">
          <p className="font-serif text-xl text-forest">{expired ? "Renew your membership" : "Activate your annual membership"}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Send <span className="font-semibold text-forest">${PRICE}</span> via <span className="font-semibold text-forest">Zelle</span> to:
          </p>
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-emerald/30 bg-card px-4 py-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Providence Partners · Zelle (email)</p>
              <p className="mt-0.5 break-all font-mono text-base font-semibold text-forest sm:text-lg" data-testid="zelle-email">{ZELLE_EMAIL}</p>
            </div>
            <Button size="sm" variant="outline" onClick={copyEmail} data-testid="zelle-copy-btn" className="rounded-full">
              <Copy className="mr-1 h-3.5 w-3.5" /> {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <ol className="mt-4 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Send <strong className="text-forest">${PRICE}</strong> via Zelle to the email above.</li>
            <li>Put your facility name in the Zelle memo: <em>"{facility.name || "(your facility name)"}"</em></li>
            <li>We'll activate your annual membership and email a receipt within 1 business day.</li>
          </ol>
          <p className="mt-3 text-xs italic text-muted-foreground">
            Questions? Call (586) 306-8449 — always available, 24/7.
          </p>
        </div>
      )}
      {isActive && (
        <p className="mt-5 text-xs text-muted-foreground">
          Your membership auto-expires in {daysLeftActive} days. We'll email a renewal reminder 30 days before expiration. To renew, send $299 via Zelle to {ZELLE_EMAIL}.
        </p>
      )}
    </Card>
  );
}
